import React from 'react';
import { Box, useTheme } from '@mui/material';

import grao from '../assets/grao.png';

/**
 * O fundo que o vidro refrata.
 *
 * Vidro sobre preto chapado é invisível: sem nada atrás, `backdrop-filter` não
 * tem o que desfocar e a superfície vira um retângulo cinza. Esta camada dá ao
 * produto o que refratar — três campos de cor muito amplos e muito fracos,
 * ancorados nos cantos, mais um grão fino que mata o degradê em faixas.
 *
 * POR QUE ELA É ESTÁTICA. A tentação é animar a aurora, e é exatamente o que
 * faz um produto financeiro parecer papel de parede. O movimento deste produto
 * está onde ele significa alguma coisa — o número que sobe até o valor, a barra
 * que preenche, o painel que entra. O fundo fica quieto, e é por isso que o
 * resto é notado.
 *
 * CUSTO: três `radial-gradient` pintados uma vez numa camada fixa, sem
 * repintura, sem `filter`, sem animação. O grão é um SVG de 140px em `data:`,
 * ladrilhado pelo próprio navegador.
 *
 * O grão importa mais do que parece: um degradê escuro de 1500px em tela de 8
 * bits mostra anéis de banding, e anel de banding é a diferença entre "caro" e
 * "gradiente de template".
 */
// O GRÃO É UM LADRILHO RASTERIZADO, e não um filtro SVG.
//
// A versão anterior desenhava o ruído com `feTurbulence`: o navegador recalcula
// o filtro a cada repintura da camada, e a mesma semente rende texturas
// diferentes entre motores — o acabamento mudava de máquina para máquina, que
// é exatamente o que a superfície de um produto não pode fazer. O ladrilho de
// 64x64 em `assets/grao.png` é gerado uma vez, determinístico, e o navegador
// só o repete.
const GRAO = `url(${grao})`;

const AuroraDeFundo = () => {
  const tema = useTheme();
  const escuro = tema.palette.mode === 'dark';

  const campos = escuro
    ? [
        // azul-noite alto à esquerda: é o campo que o cartão da leitura refrata
        'radial-gradient(1200px 720px at 8% -6%, rgba(60, 104, 200, 0.30), transparent 62%)',
        // verde-petróleo baixo à direita: dá temperatura ao rodapé sem virar cor de marca
        'radial-gradient(1000px 640px at 96% 8%, rgba(26, 110, 118, 0.17), transparent 60%)',
        // brasa muito fraca no pé da página, para o fim da rolagem não ser um poço
        'radial-gradient(1100px 700px at 60% 108%, rgba(132, 66, 104, 0.20), transparent 62%)'
      ]
    : [
        'radial-gradient(1200px 720px at 6% -8%, rgba(120, 160, 226, 0.28), transparent 60%)',
        'radial-gradient(1000px 640px at 98% 6%, rgba(118, 190, 196, 0.22), transparent 58%)',
        'radial-gradient(1100px 700px at 58% 110%, rgba(206, 170, 190, 0.20), transparent 60%)'
      ];

  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundColor: 'background.default',
          backgroundImage: campos.join(', ')
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          opacity: escuro ? 0.05 : 0.035,
          backgroundImage: GRAO,
          backgroundRepeat: 'repeat',
          backgroundSize: '64px 64px',
          mixBlendMode: escuro ? 'overlay' : 'multiply'
        }}
      />
    </>
  );
};

export default AuroraDeFundo;
